/**
 * drawOffsets.ts — Visual draw offsets for entities.
 *
 * Each offset defines the destination rectangle relative to the entity's
 * hitbox position, so sprites appear larger than colliders and are
 * correctly centred/grounded.
 *
 * @module drawOffsets
 */

import { NOVA_HEIGHT, NOVA_WIDTH } from '../../constants/gameConstants';

/** Draw offset for a single entity type. */
export interface DrawOffset {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

// ── Nova ──────────────────────────────────────────────────────────────────

// DECISION: v2 pixel-art frames are 60×60 but the character content is small
// (~14×28 px). Draw at 65×65 (90% of previous 72) so Nova feels proportionate
// to the world. getNormalizedSheet bottom-aligns all frames to the median foot
// row (45). NOVA_FEET_RATIO tuned down from 46/60 to 45/60 to compensate for
// sub-pixel rounding at the smaller draw size — prevents 1px ground float.
const NOVA_DRAW_SIZE = 65;
const NOVA_FEET_RATIO = 45 / 60;
export const NOVA_DRAW_OFFSET: DrawOffset = {
  x: -(NOVA_DRAW_SIZE - NOVA_WIDTH) / 2,
  y: NOVA_HEIGHT - NOVA_FEET_RATIO * NOVA_DRAW_SIZE,
  w: NOVA_DRAW_SIZE,
  h: NOVA_DRAW_SIZE,
};

// ── Enemies ───────────────────────────────────────────────────────────────
// DECISION: Enemy sprites are drawn larger than their hitboxes and centered.
// drawWidth/drawHeight preserve the cropped frame aspect ratio.
// x centres the sprite horizontally on the hitbox.
// y aligns the sprite bottom with the hitbox bottom (feet on ground).

// Nibbler: hitbox 24×24, cropped frame 400×300 (~1.33:1 → wide)
// Sprites have been horizontally centered and vertically aligned to the bottom (Y=290 out of 300).
const NIBBLER_DRAW_W = 30; // Reduced to ~75% size
const NIBBLER_DRAW_H = Math.round(NIBBLER_DRAW_W * (300 / 400));
// There are 10 pixels of empty padding below the sprite in the 400x300 frame, push it down so the feet touch the 24px floor boundary
const NIBBLER_FOOT_CORRECTION = Math.max(1, Math.round((10 / 300) * NIBBLER_DRAW_H)) + 2; 

export const NIBBLER_DRAW_OFFSET: DrawOffset = {
  x: -(NIBBLER_DRAW_W - 24) / 2,
  y: 24 - NIBBLER_DRAW_H + NIBBLER_FOOT_CORRECTION,
  w: NIBBLER_DRAW_W,
  h: NIBBLER_DRAW_H,
};

// Thornhop: hitbox 22×28, frame 400x400 with 87px+ padding.
// DECISION: Nibbler draws at 30×23. Thornhop should be slightly bigger
// (chunky hopper). 42×42 ≈ nibbler +40%.
const THORNHOP_DRAW_W = 42;
const THORNHOP_DRAW_H = 42;
// 15px bottom padding in the 400px frame → (15/400)*42 ≈ 1.6 → +1 for rounding
const THORNHOP_FOOT_CORRECTION = Math.round((15 / 400) * THORNHOP_DRAW_H) + 1;
export const THORNHOP_DRAW_OFFSET: DrawOffset = {
  x: -(THORNHOP_DRAW_W - 22) / 2,
  y: 28 - THORNHOP_DRAW_H + THORNHOP_FOOT_CORRECTION,
  w: THORNHOP_DRAW_W,
  h: THORNHOP_DRAW_H,
};

// GloomBat: hitbox 28×20, frame 400x400
// Since these are 400x400 perfectly centered, let's size it back up
const GLOOM_BAT_DRAW_W = 38; // Drawn slightly larger than hitbox for wing overhang
const GLOOM_BAT_DRAW_H = 38; // Square frame
export const GLOOM_BAT_DRAW_OFFSET: DrawOffset = {
  x: -(GLOOM_BAT_DRAW_W - 28) / 2,
  y: -(GLOOM_BAT_DRAW_H - 20) / 2, // Centered vertically on the hitbox
  w: GLOOM_BAT_DRAW_W,
  h: GLOOM_BAT_DRAW_H,
};
